// Presentación de texto clínico que en la base vive en MAYÚSCULAS.
//
// POR QUÉ NO SE HACE CON CSS: `text-transform: capitalize` sobre
// "LIZ CASTRO CONTRERAS" devuelve "LIZ CASTRO CONTRERAS" -- capitalize sólo
// toca la PRIMERA letra de cada palabra y no baja el resto. Y aunque lo
// hiciera, dejaría "Y", "De", "La" en mayúscula y rompería las tildes de los
// nombres que sí vienen bien escritos.
//
// EL DATO ORIGINAL NO SE MODIFICA: esto es sólo para pintar.

// Partículas que van en minúscula cuando NO son la primera palabra.
// "TRAUMATOLOGIA Y ORTOPEDIA" -> "Traumatología y Ortopedia", no "Y Ortopedia".
const PARTICULAS = new Set([
    "y", "e", "o", "u",
    "de", "del", "la", "las", "el", "los",
    "al", "a", "en", "con", "por", "para",
]);

/**
 * Convierte a Title Case respetando partículas, tildes y separadores internos.
 *
 * Sólo transforma cuando el texto viene TODO EN MAYÚSCULAS. Si ya trae mezcla
 * de mayúsculas y minúsculas, se devuelve intacto: significa que alguien lo
 * escribió con cuidado y no nos toca "corregirlo" (p. ej. "Andrea Nicole
 * Aguilar Santana", que en la base ya está bien).
 */
export function tituloClinico(texto) {
    if (typeof texto !== "string") return "";
    const limpio = texto.trim();
    if (!limpio) return "";

    // ¿Tiene alguna minúscula? Entonces ya está formateado: no se toca.
    if (limpio !== limpio.toLocaleUpperCase("es")) return limpio;

    // Se parte conservando los separadores (espacios, guiones, barras, puntos)
    // para poder recomponer el texto exactamente como venía.
    const piezas = limpio.toLocaleLowerCase("es").split(/([\s\-/.,()]+)/);

    let esPrimeraPalabra = true;
    // Una partícula sólo lo es si viene separada por ESPACIO: tras un punto o un
    // guion estamos dentro de una sigla o de un compuesto.
    let separadaPorEspacio = true;

    return piezas
        .map((pieza, i) => {
            if (/^[\s\-/.,()]+$/.test(pieza)) {
                // Tras abrir paréntesis o barra, la siguiente palabra vuelve a
                // ser un inicio ("ODONTOLOGIA (PROTESIS)").
                if (/[(/]/.test(pieza)) esPrimeraPalabra = true;
                separadaPorEspacio = /\s/.test(pieza);
                return pieza;
            }
            if (!pieza) return pieza;

            // Una letra suelta SEGUIDA DE PUNTO es la inicial de una sigla, no
            // una conjunción: "BODEGA DE U.T.I" -> "Bodega de U.T.I", jamás
            // "u.T.I". Sin este lookahead, la "u" de U.T.I caería en PARTICULAS.
            const inicialDeSigla =
                pieza.length === 1 && /^\./.test(piezas[i + 1] || "");

            const esParticula =
                PARTICULAS.has(pieza) && separadaPorEspacio && !inicialDeSigla;
            const resultado =
                esParticula && !esPrimeraPalabra
                    ? pieza
                    : pieza.charAt(0).toLocaleUpperCase("es") + pieza.slice(1);

            esPrimeraPalabra = false;
            return resultado;
        })
        .join("");
}

/**
 * Separa el código que muchas especialidades llevan pegado al final del nombre:
 * "MEDICINA GENERAL (CE)" -> { nombre: "Medicina General", sigla: "CE" }
 *
 * En la base el código es PARTE DEL NOMBRE, no un campo aparte, y así se queda:
 * esta separación ocurre sólo al pintar. En datos reales aparecen al menos CE,
 * HO y CO, y hay especialidades sin código ninguno ("DISPENSARIOS ANEXOS"),
 * que devuelven `sigla: null`.
 *
 * Sólo se separa si el paréntesis final contiene 2-3 letras mayúsculas. Un
 * nombre como "ODONTOLOGIA (Protesis)" NO es un código: se queda entero.
 */
export function separarEspecialidad(nombreCompleto) {
    if (typeof nombreCompleto !== "string") return { nombre: "", sigla: null };
    const limpio = nombreCompleto.trim();
    if (!limpio) return { nombre: "", sigla: null };

    const coincidencia = limpio.match(/^(.*?)\s*\(([A-Z]{2,3})\)\s*$/);
    if (!coincidencia) return { nombre: tituloClinico(limpio), sigla: null };

    return {
        nombre: tituloClinico(coincidencia[1]),
        sigla: coincidencia[2],
    };
}
